import { SkillDevelopmentItemCommunication } from '../libs/SkillDevelopmentItemInfoCommunication'
import { SkillDevelopmentItemHelping } from '../libs/SkillDevelopmentItemInfoHelping'
import { ProgressLevel } from '../enums/ProgressLevel'
import { Skill } from '../enums/Skill'
import { SkillDevelopmentTaskPerformance } from '../libs/SkillDevelopmentItemInfoTaskPerformance'
import { BaseObject } from './types'
import { SkillDevelopmentItemDecisionMaking } from '../libs/SkillDevelopmentItemInfoDecisionMaking'
import { Slot, SlotDataPoint } from './Slot'
import { SkillDevelopmentItemInfo, SkillInfoDevelopmentItemInfoKey } from '../libs/SkillDevelopmentItemInfo'
import { RecommendationLevels } from '../enums/RecommendationLevels'
import { SessionAIRecoveryState } from './SessionAIRecovery'

type TeamNormalizedMetric =
  | 'teamSharedResource'
  | 'teamSharedAbility'
  | 'teamSharedItem'
  | 'teamTimesTalkedTotal'
  | 'teamTimesTalkedPreplanning'
  | 'teamTimesTalkedExecution'
  | 'teamTimesTalkedEvaluation'
  | 'teamReceipt'
  | 'teamAskQuestionOpen'
  | 'teamAskQuestionClarifyConfirm'
  | 'teamInformReport'
  | 'teamAgreeSupport'
  | 'teamDisagreeChallengeIssue'
  | 'teamPlanNextStep'
  | 'teamDelegateAssign'
  | 'teamAskForHelp'
  | 'teamOfferHelp'
  | 'teamInvite'
  | 'teamRoleTalk'
  | 'teamAdmitMistake'
  | 'teamThank'
  | 'teamConstructiveFeedback'
  | 'teamGoalOrientation'
  | 'teamTimeAtMap'
  | 'teamAirtimeTotal'
  | 'teamAirtimePreplanning'
  | 'teamAirtimeExecution'
  | 'teamAirtimeEvaluation'
  | 'teamAirtimeRelativeTotal'
  | 'teamAirtimeRelativePreplanning'
  | 'teamAirtimeRelativeExecution'
  | 'teamAirtimeRelativeEvaluation'
  | 'teamOverlapAirtimeTimesTotal'
  | 'teamOverlapAirtimeTimesPreplanning'
  | 'teamOverlapAirtimeTimesExecution'
  | 'teamOverlapAirtimeTimesEvaluation'
  | 'teamTimeParalyzed'
  | 'teamInactiveTimeOutside'
  | 'teamInactiveTimeInside'

type TeamReflectionMetric =
  | 'teamReflectionQuestionOne'
  | 'teamReflectionQuestionTwo'
  | 'teamReflectionQuestionThree'
  | 'teamReflectionQuestionFour'
  | 'teamReflectionQuestionFive'
  | 'teamReflectionQuestionSix'
  | 'teamReflectionQuestionSeven'

/** The six team survey questions that make up the team's psychological safety, in chart order. */
export const TEAM_PSYCHOLOGICAL_SAFETY_KEYS = [
  'teamReflectionQuestionOne',
  'teamReflectionQuestionTwo',
  'teamReflectionQuestionThree',
  'teamReflectionQuestionFour',
  'teamReflectionQuestionFive',
  'teamReflectionQuestionSix'
] as const satisfies readonly TeamReflectionMetric[]

export type TeamPsychologicalSafetyKey = (typeof TEAM_PSYCHOLOGICAL_SAFETY_KEYS)[number]

/** Every question of the team survey: the six of psychological safety and the one about using strengths. */
export const TEAM_SURVEY_KEYS = [
  ...TEAM_PSYCHOLOGICAL_SAFETY_KEYS,
  'teamReflectionQuestionSeven'
] as const satisfies readonly TeamReflectionMetric[]

export type TeamDatapointMetric = TeamNormalizedMetric | TeamReflectionMetric | 'teamHealth' | 'uridiumPer15Min'

export type TeamDatapoints = Partial<Record<TeamDatapointMetric, number>>
export type TeamDatapointsRaw = Record<string, number>

type NormalizedPlayerMetric =
  | 'playerSharedResource'
  | 'playerSharedAbility'
  | 'playerSharedItem'
  | 'playerTimesTalkedTotal'
  | 'playerTimesTalkedPreplanning'
  | 'playerTimesTalkedExecution'
  | 'playerTimesTalkedEvaluation'
  | 'playerReceipt'
  | 'playerAskQuestionOpen'
  | 'playerAskQuestionClarifyConfirm'
  | 'playerInformReport'
  | 'playerAgreeSupport'
  | 'playerDisagreeChallengeIssue'
  | 'playerPlanNextStep'
  | 'playerDelegateAssign'
  | 'playerAskForHelp'
  | 'playerOfferHelp'
  | 'playerInvite'
  | 'playerRoleTalk'
  | 'playerAdmitMistake'
  | 'playerThank'
  | 'playerConstructiveFeedback'
  | 'playerGoalOrientation'
  | 'playerTimeAtMap'
  | 'playerAirtimeTotal'
  | 'playerAirtimePreplanning'
  | 'playerAirtimeExecution'
  | 'playerAirtimeEvaluation'
  | 'playerAirtimeRelativeTotal'
  | 'playerAirtimeRelativePreplanning'
  | 'playerAirtimeRelativeExecution'
  | 'playerAirtimeRelativeEvaluation'
  | 'playerOverlapAirtimeTimesTotal'
  | 'playerOverlapAirtimeTimesPreplanning'
  | 'playerOverlapAirtimeTimesExecution'
  | 'playerOverlapAirtimeTimesEvaluation'
  | 'playerTimeParalyzed'
  | 'playerInactiveTimeOutside'
  | 'playerInactiveTimeInside'

type PlayerReflectionMetric =
  | 'playerReflectionQuestionOne'
  | 'playerReflectionQuestionTwo'
  | 'playerReflectionQuestionThree'
  | 'playerReflectionQuestionFour'
  | 'playerReflectionQuestionFive'
  | 'playerReflectionQuestionSix'
  | 'playerReflectionQuestionSeven'
  | 'playerReflectionQuestionEight'
  | 'playerReflectionQuestionNine'
  | 'playerReflectionQuestionTen'
  | 'playerReflectionQuestionEleven'
  | 'playerReflectionQuestionTwelve'

/** The seven survey questions that make up one person's psychological safety, in chart order. */
export const INDIVIDUAL_PSYCHOLOGICAL_SAFETY_KEYS = [
  'playerReflectionQuestionOne',
  'playerReflectionQuestionTwo',
  'playerReflectionQuestionThree',
  'playerReflectionQuestionFour',
  'playerReflectionQuestionFive',
  'playerReflectionQuestionSix',
  'playerReflectionQuestionSeven'
] as const satisfies readonly PlayerReflectionMetric[]

export type IndividualPsychologicalSafetyKey = (typeof INDIVIDUAL_PSYCHOLOGICAL_SAFETY_KEYS)[number]

/** Where the apps' copy names a psychological-safety survey question, by its key: a team's first, then a person's. */
export const PSYCHOLOGICAL_SAFETY_QUESTION_COPY_PATHS = [
  'gamer.feedback.psychologicalSafety',
  'gamer.feedback.sections.experience.psychologicalSafety.questions'
] as const

/** The names those paths give the survey questions, by key. */
export type SurveyQuestionLabels = Record<string, string>

export type IndividualDatapointMetric = NormalizedPlayerMetric | PlayerReflectionMetric

export type IndividualAIFeedback = {
  whatIsWorkingWell?: string
  whatToWatchOut?: string
  tryThisNextTime?: string
  communicatedEmotionText?: string
  dominantEmotionUtterance?: string
}

export type IndividualDatapointAggregate = Partial<Record<IndividualDatapointMetric, number>> & {
  dominantEmotion?: string
  aiFeedback?: IndividualAIFeedback
}

export type IndividualDatapointAggregateRaw = Record<string, number>

export type IndividualsDatapoints = Record<string, IndividualDatapointAggregate>
export type IndividualsDatapointsRaw = Record<string, IndividualDatapointAggregateRaw>

export type CreateSessionRequest = {
  id: string
  status?: 'inactive' | 'active' | 'completed'
  teamId: string
  activityId?: string
  sprintId?: string
}

export type SessionAchievementUnlock = {
  achievementId: string
  tier: string
  slotId: string
}

/**
 * Voice processing status for a session.
 * Used to inform frontend about the state of voice analysis.
 */
export enum VoiceProcessingStatus {
  /** Voice processing is currently in progress */
  IN_PROGRESS = 'in_progress',
  /** Voice processing completed successfully */
  COMPLETED = 'completed',
  /** Voice processing failed due to missing data (no utterances, no active sessions, etc.) */
  FAILED_MISSING_DATA = 'failed_missing_data',
  /** Voice processing failed due to an error */
  FAILED_ERROR = 'failed_error'
}

/** Where a game's written feedback stands for one player who played in it. */
export enum SessionFeedbackState {
  READY = 'ready',
  BEING_PREPARED = 'being_prepared',
  /** The analysis finished and wrote nothing for them, as for a player nothing was picked up from in the call. */
  NOTHING_WRITTEN = 'nothing_written',
  ANALYSIS_FAILED = 'analysis_failed',
  /** The call was never analysed, long after the game. */
  NOT_ANALYSED = 'not_analysed'
}

export type Session = BaseObject &
  CreateSessionRequest & {
    id: string
    courseId?: string
    hasSlots?: boolean
    teams_datapoints?: TeamDatapoints
    teams_datapoints_raw?: TeamDatapointsRaw
    individuals_datapoints?: IndividualsDatapoints
    individuals_datapoints_raw?: IndividualsDatapointsRaw
    achievementsUnlocked?: Record<string, SessionAchievementUnlock[]> // userId -> achievements
    /** Status of voice processing for this session */
    voiceProcessingStatus?: VoiceProcessingStatus
    /** Tracks partial AI feedback failures queued for durable recovery */
    voiceAIRecovery?: SessionAIRecoveryState
    /** Whereby transcription id whose analysis currently populates this session */
    voiceSourceTranscriptionId?: string
    /** Covered time span (ms) of the winning transcription — guards against shorter ones clobbering it */
    voiceTranscriptionCoveredMs?: number
    /** Message count of the winning transcription — coverage tie-breaker */
    voiceTranscriptionMessageCount?: number
    /** Set while a transcription is being applied; a second transcription waits until the claim is stale or released */
    voiceProcessingClaimedAt?: string
    /** Transcription that holds the current processing claim */
    voiceProcessingTranscriptionId?: string
    /** When an analysis was last written to this session; results built before it are out of date */
    voiceProcessingCompletedAt?: string
    /** Stamped by an operator's Back Office close; every player's game page leaves the game on it. */
    forceClosedAt?: string
    forceClosedByUserId?: string
  }

export type SessionWithSlots = Session & { slots: Slot[] }

export type SessionInfo = {
  createdAt: string
  sessionId: string
  session: SessionWithSlots
  previousSessions: SessionWithSlots[]
  name: string
  sessionNumber: number
  userOverallScore: number
  userTop1Potential?: SessionInfoPotential
  teamTop1Potential?: SessionInfoPotential
  userProgressLevel: ProgressLevel
  skills: {
    [Skill.TaskPerformance]: BaseSessionInfoSkillItem & {
      items: {
        [property in SkillDevelopmentTaskPerformance]: SkillItemProgress
      }
    }
    [Skill.Communication]: BaseSessionInfoSkillItem & {
      items: {
        [property in SkillDevelopmentItemCommunication]: SkillItemProgress
      }
    }
    [Skill.HelpingAndAssisting]: BaseSessionInfoSkillItem & {
      items: {
        [property in SkillDevelopmentItemHelping]: SkillItemProgress
      }
    }
    [Skill.DecisionMaking]: BaseSessionInfoSkillItem & {
      items: {
        [property in SkillDevelopmentItemDecisionMaking]: SkillItemProgress
      }
    }
  }
}

export type SessionInfoPotential = {
  skill: Skill
  skillDevelopmentItemKey: SkillInfoDevelopmentItemInfoKey
  recommendationLevel?: RecommendationLevels
}

export type SkilItemProgressCalculatedBy = SkillItemProgressCalculatedByItem & {
  children: SkillItemProgressCalculatedByItem[]
}

export type SkillItemProgressCalculatedByItem = {
  weight: number
  key?: keyof SlotDataPoint
  value: number
  normalized: unknown
  viktas: number
  slotId: string
}

export type SkillItemProgress = {
  uid: string
  teamScore: number
  yourScore: number
  yourScoreNormalized: number
  yourPotential: number
  teamPotential: number
  yourScoreRatio: number
  teamDisplayValue: number
  userDisplayValue: number
  userDisplayValuePerSlot: number
  formattedUserDisplayValue: string
  formattedTeamDisplayValue: string
  teamRelativeProgressPercentage?: number
  teamRelativeProgressPercentageProgressLevel?: ProgressLevel
  yourRelativeProgressPercentage?: number
  yourRelativeProgressPercentageProgressLevel?: ProgressLevel
  _skillDevelopmentItemInfo?: SkillDevelopmentItemInfo
  metaData: {
    calculatedBy: SkilItemProgressCalculatedBy[]
  }
}

export type SkillItemProgressWithKeys = SkillItemProgress & {
  skill: Skill
  skillDevelopmentItemKey:
    | keyof SkillDevelopmentItemCommunication
    | keyof SkillDevelopmentItemHelping
    | keyof SkillDevelopmentItemDecisionMaking
    | keyof SkillDevelopmentTaskPerformance
}

export type BaseSessionInfoSkillItem = {
  userSkillScore: number
  teamSkillScore: number
  teamProgressLevel: ProgressLevel
  userProgressLevel: ProgressLevel
  metaData: {
    calculatedBy: unknown
  }
  userTop1Potential: SessionInfoPotential
}

/** Close a game session: the game's own `/Session/Close` and the Back Office operator action share this. */
export type CloseGameSessionRequest = {
  teamId: string
  sessionId: string
}

export type CloseGameSessionResponse = {
  ok: boolean
  teamId: string
  sessionId: string
  previousStatus?: Session['status']
  status: 'completed'
  /** The session was already completed — nothing was written. */
  alreadyCompleted: boolean
}
